import * as React from 'react';

import PropTypes from 'prop-types';
import * as CKEditor from '@ckeditor/ckeditor5-react';
import { TextField } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import FormControl from '@material-ui/core/FormControl';
import { withStyles } from '@material-ui/core/styles';
import ExtendedEditor from 'eagle-intranet-editor';
import classNames from 'classnames';
import { clientConfig } from 'config/client';

import { Transition } from '../../components/transition/Transition';
import { IEditor, IProps, IState } from './interfaces';
import { uploadFile } from './redux';

import styles from './Editor.module.styl';

const stylesRoot = () => ({
  changeRoot: {
    opasity: '100%',
  },
});

class Editor extends React.Component<IProps, IState> {
  static propTypes: { classes: PropTypes.Validator<object> };

  public constructor(props: IProps) {
    super(props);
    this.state = {
      htmlText: props.input.value,
      isHtml: false,
      isVideoUploading: false,
      zIndexValue: '',
    };
  }

  public render() {
    const { meta, title } = this.props;
    const { isVideoUploading, zIndexValue } = this.state;
    const { classes } = this.props;

    return (
      <div className={classes.changeRoot}>
        <FormControl
          variant="outlined"
          fullWidth={true}
          margin="normal"
          error={meta.touched && meta.error}
          style={{ zIndex: zIndexValue }}
        >
          {title && (
            <legend className={styles.label}>
              <span>{title}</span>
            </legend>
          )}
          {isVideoUploading && (
            <div className={styles.videoLoaderWrapper}>
              <span>Video is uploading. Please wait</span>
            </div>
          )}
          <div className={classNames(styles.root)}>
            {this.renderEditorComponent()}
            {this.renderPreviewSourceDialog()}
          </div>
          {meta.touched && meta.error && (
            <span className={styles.error}>{meta.error}</span>
          )}
        </FormControl>
      </div>
    );
  }

  private readonly renderEditorComponent = () => {
    const { input } = this.props;

    return (
      <CKEditor
        editor={ExtendedEditor}
        data={input.value || ''}
        onInit={this.insertEditor}
        onChange={this.handleChange}
        onBlur={() => {
          this.setState({
            zIndexValue: '',
          });
        }}
        config={{
          ckfinder: {
            downloadUrl: `${clientConfig.apiPath}/files/download`,
            onError: console.error,
            onStart: this.showLoader,
            onSuccess: this.hideLoader,
            uploadFile,
          },
          sourceCode: {
            handler: this.openHtmlEditor,
          },
        }}
      />
    );
  };

  private readonly renderPreviewSourceDialog = () => {
    const { isHtml, htmlText } = this.state;

    return (
      <Dialog
        open={isHtml}
        onClose={this.closeHtmlEditor}
        TransitionComponent={Transition}
        scroll="body"
        fullScreen={true}
      >
        <div className={styles.htmlEditorWrapper}>
          <TextField
            multiline={true}
            className={styles.input}
            variant="outlined"
            value={htmlText}
            onChange={this.handleHtmlChange}
          />
          <div className={styles.buttons}>
            <Button
              variant="contained"
              type="button"
              color="primary"
              onClick={this.applyHtml}
            >
              Save
            </Button>
            <Button
              variant="contained"
              type="button"
              color="primary"
              onClick={this.closeHtmlEditor}
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>
    );
  };

  private readonly showLoader = () => {
    this.setState({
      isVideoUploading: true,
    });
  };

  private readonly hideLoader = () => {
    this.setState({
      isVideoUploading: false,
    });
  };

  private readonly applyHtml = () => {
    const { htmlText } = this.state;
    const {
      input: { onChange },
    } = this.props;

    onChange(htmlText);

    this.closeHtmlEditor();
  };

  private readonly closeHtmlEditor = () => {
    this.setState({
      htmlText: this.props.input.value,
      isHtml: false,
    });
  };

  private readonly handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    editor: IEditor,
  ) => {
    const { input } = this.props;
    const data = editor && editor.getData();

    input.onChange(data || null);
  };

  private readonly handleHtmlChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    this.setState({
      htmlText: e.target.value,
    });
  };

  private readonly insertEditor = (editor: IEditor) => {
    editor.ui
      .getEditableElement()
      .parentElement.insertBefore(
        editor.ui.view.toolbar.element,
        editor.ui.getEditableElement(),
      );

    const dropdownsInTheToolbar = editor.ui.view.toolbar.items.filter(
      (item: any) => {
        // Duck typing to find instances of DropdownView only.
        return item.panelView && item.buttonView;
      },
    );

    editor.ui.view.set('isAnyDropdownOpen', false);
    editor.ui.view
      .bind('isAnyDropdownOpen')
      .toMany(dropdownsInTheToolbar, 'isOpen', (...areOpen: any) => {
        return areOpen.some((isDropdownOpen: any) => isDropdownOpen);
      });

    editor.ui.view.on('change:isAnyDropdownOpen', () => {
      const isOpen = editor.ui.view.isAnyDropdownOpen;

      if (isOpen) {
        this.setState({
          zIndexValue: '9999',
        });
      } else {
        this.setState({
          zIndexValue: '',
        });
      }
    });
  };

  private readonly openHtmlEditor = () => {
    const {
      input: { value: htmlText },
    } = this.props;

    this.setState({
      htmlText,
      isHtml: true,
    });
  };
}

Editor.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(stylesRoot)(Editor);
